/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@nordcloud/gnui";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { ABConfiguration } from "~/models";
import { ACTIONS, Can } from "~/routing";

type Props = {
  addConfig: (newConfig: ABConfiguration) => Promise<void>;
};

function ABConfigurationAdd({ addConfig }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm();
  const watcher = methods.watch();

  const onSubmit = async (values: ABConfiguration) => {
    setIsLoading(true);
    try {
      await addConfig(values);
    } finally {
      setIsLoading(false);
      setIsSidebarOpen(false);
    }
  };

  const abLinkPattern = /^https:\/\/(autobackup\.[\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  const abLinkMsg =
    "Please provide a correct AutoBackup link (starting with https://autobackup)";

  return (
    <>
      <Can action={ACTIONS.EDIT_APPLICATION}>
        <Button
          size="md"
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="plus"
          onClick={() => setIsSidebarOpen(true)}
        >
          Add configuration
        </Button>
      </Can>

      <SidebarForm
        title="Add AutoBackup configuration"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="customerName" label="Customer name" required />
        <SidebarTextField
          name="setup"
          label="Setup link"
          registerOptions={{
            pattern: { value: abLinkPattern, message: abLinkMsg },
          }}
          required
        />
        <SidebarTextField
          name="policy"
          label="Policy link"
          registerOptions={{
            pattern: { value: abLinkPattern, message: abLinkMsg },
          }}
          required
        />

        <SidebarFormButtons
          submitText="Add"
          submitEnabled={
            !!(watcher.customerName && watcher.setup && watcher.policy)
          }
          submitLoading={isLoading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}

export { ABConfigurationAdd };
